import React from 'react';

interface DataFlowDiagramProps {
    variant?: 'full' | 'compact';
    className?: string;
}

const DataFlowDiagram: React.FC<DataFlowDiagramProps> = ({ variant = 'full', className = '' }) => {
    const devices = [
        { label: 'WHOOP', x: 60 },
        { label: 'Garmin', x: 170 },
        { label: 'Oura', x: 280 },
        { label: 'VALD', x: 390 },
        { label: 'Hawkins', x: 500 },
    ];

    const outputs = [
        { label: 'Readiness', color: '#22C55E', x: 100 },
        { label: 'Recovery', color: '#3B82F6', x: 230 },
        { label: 'Workload', color: '#EAB308', x: 360 },
        { label: 'Insights', color: '#8B5CF6', x: 470 },
    ];

    if (variant === 'compact') {
        return (
            <div className={`w-full ${className}`}>
                <svg viewBox="0 0 400 200" className="w-full h-auto" fill="none">
                    {/* Devices row */}
                    {[
                        { label: 'WHOOP', x: 40 },
                        { label: 'Garmin', x: 120 },
                        { label: 'Oura', x: 200 },
                        { label: 'VALD', x: 280 },
                        { label: 'Hawkins', x: 360 },
                    ].map((d) => (
                        <g key={d.label}>
                            <rect x={d.x - 28} y={10} width="56" height="28" stroke="rgba(255,255,255,0.3)" strokeWidth="1" rx="0" />
                            <text x={d.x} y="28" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="8" fontFamily="Inter, sans-serif">{d.label}</text>
                            <line x1={d.x} y1="38" x2={d.x} y2="70" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="3 3" className="animate-dash-flow" />
                        </g>
                    ))}

                    {/* CGM Hub */}
                    <rect x="12" y="70" width="376" height="36" stroke="white" strokeWidth="1.5" />
                    <text x="200" y="93" textAnchor="middle" fill="white" fontSize="11" fontWeight="600" fontFamily="Inter, sans-serif">CGM SPORTS</text>

                    {/* Output lines */}
                    {[
                        { label: 'Readiness', color: '#22C55E', x: 110 },
                        { label: 'Recovery', color: '#3B82F6', x: 200 },
                        { label: 'Workload', color: '#EAB308', x: 290 },
                    ].map((o) => (
                        <g key={o.label}>
                            <line x1={o.x} y1="106" x2={o.x} y2="140" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="3 3" className="animate-dash-flow" />
                            <rect x={o.x - 32} y="140" width="64" height="28" stroke={o.color} strokeWidth="1" opacity="0.8" />
                            <text x={o.x} y="158" textAnchor="middle" fill={o.color} fontSize="8" fontFamily="Inter, sans-serif">{o.label}</text>
                        </g>
                    ))}
                </svg>
            </div>
        );
    }

    return (
        <div className={`w-full ${className}`}>
            <svg viewBox="0 0 560 280" className="w-full h-auto" fill="none">
                {/* Device input row */}
                {devices.map((device) => (
                    <g key={device.label}>
                        <rect x={device.x - 35} y={20} width="70" height="36" stroke="rgba(255,255,255,0.3)" strokeWidth="1" rx="0" />
                        <text x={device.x} y={43} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="11" fontFamily="Inter, sans-serif" fontWeight="500">
                            {device.label}
                        </text>
                        {/* Flowing connection line to hub */}
                        <line x1={device.x} y1={56} x2={device.x} y2={100} stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4 4" className="animate-dash-flow" />
                    </g>
                ))}

                {/* CGM Hub - center box */}
                <rect x="25" y="100" width="510" height="50" stroke="white" strokeWidth="2" />
                <text x="280" y="130" textAnchor="middle" fill="white" fontSize="16" fontWeight="700" fontFamily="Inter, sans-serif">
                    CGM SPORTS
                </text>
                <text x="280" y="143" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9" fontFamily="Inter, sans-serif">
                    Intelligent Performance Layer
                </text>

                {/* Output row */}
                {outputs.map((output) => (
                    <g key={output.label}>
                        <line x1={output.x} y1={150} x2={output.x} y2={200} stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4 4" className="animate-dash-flow" />
                        <rect x={output.x - 40} y={200} width="80" height="36" stroke={output.color} strokeWidth="1.5" opacity="0.8" />
                        <circle cx={output.x - 25} cy={218} r="3" fill={output.color} className="animate-data-pulse" />
                        <text x={output.x + 2} y={222} textAnchor="middle" fill={output.color} fontSize="10" fontFamily="Inter, sans-serif" fontWeight="500">
                            {output.label}
                        </text>
                    </g>
                ))}
            </svg>
        </div>
    );
};

export default DataFlowDiagram;
